import { Device } from "@models";
import { ChevronRight, Delete } from "@mui/icons-material";
import { IconButton, Typography } from "@mui/material";
import { fillRouteWithSlugs } from "@utils";
import { observer } from "mobx-react-lite";
import type { KeyboardEvent as ReactKeyboardEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { DemoChip } from "src/components/DemoChip/DemoChip";
import { ROUTE_SLUGS } from "src/config/navigation/routes";
import { useDeleteDeviceMutation } from "src/hooks/mutations";
import { useStore } from "src/stores";
import { ROUTES } from "../../config";

type Props = {
  devices: Device[];
};

const DeviceList = ({ devices }: Props) => {
  const navigate = useNavigate();
  const { deviceStore } = useStore();
  const deleteDeviceMutation = useDeleteDeviceMutation();
  const { orgId = "" } = useParams();

  const confirmationToDelete = (id: string) => {
    // confirmation popup
    let confirmed = window.confirm(
      "Are you sure you want to delete this device? All related data will be lost."
    );
    {
      confirmed &&
        deleteDeviceMutation.mutate({
          orgId,
          id,
        });
    }
  };

  const handleContinue = async (device: Device) => {
    deviceStore.setActiveGlobalDeviceId(orgId, device.id);
    navigate(
      fillRouteWithSlugs(ROUTES.DEVICE_OVERVIEW, {
        [ROUTE_SLUGS.ORG_ID]: orgId,
        [ROUTE_SLUGS.DEVICE_ID]: device.id,
      })
    );
  };

  const handleDeviceKeyDown = (
    event: ReactKeyboardEvent<HTMLDivElement>,
    device: Device
  ) => {
    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }

    event.preventDefault();
    handleContinue(device);
  };

  return (
    <div data-testid="DeviceList" className="flex flex-col gap-y-3">
      {devices.map((device) => {
        return (
          <div
            key={device.id}
            aria-label={`Open ${device.name}`}
            onClick={() => handleContinue(device)}
            onKeyDown={(event) => handleDeviceKeyDown(event, device)}
            className="flex w-full cursor-pointer items-center justify-between rounded border border-solid border-gray-200 bg-white p-5 hover:bg-zinc-50"
            role="button"
            tabIndex={0}
          >
            <div className="flex items-center gap-x-2">
              {device.isDemoDevice && <DemoChip size="tiny" />}
              <Typography variant="body1">{device.name}</Typography>
            </div>
            <div className="flex items-center gap-x-2">
              <IconButton>
                <ChevronRight />
              </IconButton>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  confirmationToDelete(device.id);
                }}
              >
                <Delete />
              </IconButton>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default observer(DeviceList);
